import { Alert, Button, Input, Spin } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux/selectors';
import { useGeneEditReportTextModalLogic } from './hooks';

export type GeneEditReportTextModalProps = {
  geneId: number;
  onSaved?: () => void;
  onClose: () => void;
  stageType?: string;
};

const GeneEditReportTextModal: React.FC<GeneEditReportTextModalProps> = ({
  geneId,
  onSaved,
  onClose,
  stageType,
}) => {
  // canEditMetadata comes from useVariantAccess() hook,
  // only true when user is in group "MetaDataEditor",
  // if false: inputs are readOnly and button is disabled
  const { isReadOnly, canEditMetadata } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    loading,
    disordersEditorState,
    setDisordersEditorState,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleSubmit,
  } = useGeneEditReportTextModalLogic({ geneId, onSaved, onClose, stageType });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          {isReadOnly && lock && (
            <Alert
              message="Read-Only Mode"
              description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              handleChange,
              handleSubmit: submit,
              setFieldValue,
              isValid,
              dirty,
            }) => {
              const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

              return (
                <form onSubmit={submit}>
                  <div className="c-form-field">
                    <div className="c-form-field__label">
                      Protein name (for report text):
                    </div>
                    <div className="c-form-field__wrapper">
                      <Input
                        name="report_text_protein"
                        type="text"
                        value={values.report_text_protein}
                        onChange={handleChange}
                        placeholder="Enter protein name"
                        readOnly={!canEditMetadata}
                      />
                    </div>
                  </div>

                  <div className="c-form-field">
                    <div className="c-form-field__label">
                      Function (for report text):
                    </div>
                    <div className="c-form-field__wrapper">
                      <Input
                        name="report_text_function"
                        type="text"
                        value={values.report_text_function}
                        onChange={handleChange}
                        placeholder="Enter function"
                        readOnly={!canEditMetadata}
                      />
                    </div>
                  </div>

                  <div className="c-form-field">
                    <div className="c-form-field__label">
                      Phenotypes (for report text):
                    </div>
                    <div className="c-form-field__wrapper">
                      <RichTextEditor
                        name="report_text_disorders"
                        editorState={disordersEditorState}
                        onChange={(newState) => {
                          setDisordersEditorState(newState);
                          const htmlContent = editorStateToHtml(newState);
                          setFieldValue('report_text_disorders', htmlContent);
                        }}
                        placeholder=""
                        label=""
                        readOnly={!canEditMetadata}
                      />
                    </div>
                  </div>

                  <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="h-proband-modal__form-submit-btn"
                      disabled={isSaveDisabled || !canEditMetadata}
                    >
                      Save changes
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default GeneEditReportTextModal;
